import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { socialMediaService } from '../../../services/socialMediaService';

const ConnectionTester = ({ connections, onRefresh }) => {
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: 'Facebook',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Facebook Graph API v18.0'
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: 'Instagram',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      description: 'Instagram Business API'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business',
      icon: 'MessageCircle',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'WhatsApp Business API'
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      description: 'LinkedIn Marketing API'
    }
  ];

  const handleTestConnection = async (platform) => {
    setTesting({ ...testing, [platform]: true });
    try {
      const result = await socialMediaService?.testConnection(platform, {});
      setTestResults({ ...testResults, [platform]: result });
    } catch (error) {
      setTestResults({
        ...testResults,
        [platform]: { success: false, message: 'Error al probar conexión' }
      });
    } finally {
      setTesting({ ...testing, [platform]: false });
    }
  };

  const getConnectionStatus = (platformId) => {
    const connection = connections?.find(c => c?.platform === platformId);
    return connection?.status || 'not_configured';
  };

  const getStatusBadge = (status) => {
    const badges = {
      connected: { label: 'Conectado', class: 'bg-green-100 text-green-800' },
      disconnected: { label: 'Desconectado', class: 'bg-red-100 text-red-800' },
      testing: { label: 'Probando...', class: 'bg-yellow-100 text-yellow-800' },
      not_configured: { label: 'No Configurado', class: 'bg-gray-100 text-gray-800' }
    };
    return badges?.[status] || badges?.not_configured;
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {platforms?.map((platform) => {
          const status = getConnectionStatus(platform?.id);
          const statusInfo = getStatusBadge(status);
          return (
            <div key={platform?.id} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <Icon name={platform?.icon} size={32} className={platform?.color} />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo?.class}`}>
                  {statusInfo?.label}
                </span>
              </div>
              <h3 className="font-semibold text-foreground">{platform?.name}</h3>
              <p className="text-xs text-muted-foreground mt-1">{platform?.description}</p>
            </div>
          );
        })}
      </div>
      {/* Connection Testing Panel */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground flex items-center">
            <Icon name="Zap" size={24} className="mr-2 text-primary" />
            Prueba de Conexiones
          </h2>
          <Button variant="outline" size="sm" iconName="RefreshCw" onClick={onRefresh}>
            Actualizar Estado
          </Button>
        </div>

        <div className="space-y-4">
          {platforms?.map((platform) => {
            const isTesting = testing?.[platform?.id];
            const result = testResults?.[platform?.id];

            return (
              <div
                key={platform?.id}
                className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className={`p-3 rounded-lg ${platform?.bgColor}`}>
                      <Icon name={platform?.icon} size={24} className={platform?.color} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-foreground">{platform?.name}</h3>
                      <p className="text-sm text-muted-foreground">{platform?.description}</p>
                      {result && (
                        <div className="mt-2">
                          <p
                            className={`text-sm font-medium ${
                              result?.success ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {result?.message}
                          </p>
                          {result?.success && result?.apiVersion && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Versión API: {result?.apiVersion}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleTestConnection(platform?.id)}
                    disabled={isTesting}
                    iconName={isTesting ? 'Loader2' : 'Play'}
                    variant="outline"
                  >
                    {isTesting ? 'Probando...' : 'Probar'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* API Health Monitoring */}
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Activity" size={24} className="mr-2 text-primary" />
          Monitoreo de Salud de APIs
        </h2>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plataforma</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Estado</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Tiempo de Respuesta</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Última Prueba</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {platforms?.map((platform) => (
                <tr key={platform?.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <Icon name={platform?.icon} size={18} className={platform?.color} />
                      <span className="text-sm font-medium text-foreground">{platform?.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Saludable
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">245ms</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">Hace 5 minutos</td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" iconName="Settings" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConnectionTester;
